import React, {
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import isEqual from "react-fast-compare";
import { useMediaQuery } from "react-responsive";
import { Handler, useGesture } from "@use-gesture/react";
import classNames from "classnames";
import useWindowScroll from "../../hooks/useWindowScroll";
import SectionMarker from "./SectionMarker";
import ContentSlide from "./ContentSlide";
import { cockpitComponent } from "../CockpitLayoutComponents";
import NextSlideButton from "./NextSlideButton";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import Icon from "../Icon";
import "./style.scss";
import { useLocation, useNavigate } from "react-router-dom";

type ContentTrackHandle = {
  getContentSlide: (index: number) => HTMLDivElement;
};

type ReactSection = {
  element: ReactElement;
  settings: {
    nav_title: string; //Used for label
    nav_id: string; //Used for anchor hash
  };
};

const ContentTrack = forwardRef<
  ContentTrackHandle,
  {
    sections: ReactSection[];
    cockpitSections: CockpitComponentData[][];
  }
>(function ContentTrackRender({ sections, cockpitSections }, ref) {
  const contentSlides = useRef<HTMLDivElement[]>([]);

  const getContentSlide = (index: number) => contentSlides.current[index];

  useImperativeHandle(ref, () => ({
    getContentSlide
  }));
  return (
    <div className="content-track">
      {sections.length > 0
        ? sections.map((section, i) => (
            <ContentSlide
              key={i}
              ref={element => {
                contentSlides.current.push(element!);
              }}
            >
              {section.element}
            </ContentSlide>
          ))
        : cockpitSections.map((section, i) => (
            <ContentSlide
              key={i}
              ref={element => {
                contentSlides.current.push(element!);
              }}
            >
              {section?.map(cockpitComponent)}
            </ContentSlide>
          ))}
    </div>
  );
});

const DeepMemoContentTrack = React.memo(ContentTrack, isEqual);

type CockpitComponentData = Record<string, unknown> & {
  component: string;
  settings: Record<string, unknown>;
  children: Record<string, unknown>[];
};

const SectionNavigation = ({
  children,
  cockpitChildren,
  direction = "vertical",
  inOverlay = false,
  showMenu = false,
  showMenuExpandButton = false,
  showTabs = false,
  className
}: {
  children?: ReactNode;
  cockpitChildren?: CockpitComponentData[];
  direction?: "horizontal" | "vertical";
  inOverlay?: boolean;
  showMenu?: boolean;
  showMenuExpandButton?: boolean;
  showTabs?: boolean;
  className?: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isTouchDevice = useMediaQuery({ query: "(pointer: coarse)" });
  const isLargeScreen = useMediaQuery({ minWidth: "1200px" });
  const isVeryLargeScreen = useMediaQuery({ minWidth: "1600px" });
  const container = inOverlay
    ? document.getElementsByClassName("overlay-content")?.[0]
    : document.documentElement;
  const scrollPos = useWindowScroll(container, direction, 100);
  const [activeIndex, setActiveIndex] = useState(0);
  const _activeIndex = useRef(activeIndex);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [hovered, setHovered] = useState(false);
  const [menuBtnClicked, setMenuBtnClicked] = useState(false);
  const [introTimer, setIntroTimer] = useState(!showMenuExpandButton);
  const [disableBodyScroll, setDisableBodyScroll] = useState(false);
  const t = useBjelinTranslations();
  const contentTrack = useRef<ContentTrackHandle>(null);
  const getSlide = useCallback(
    (i: number) => contentTrack?.current?.getContentSlide(i),
    [contentTrack]
  );
  const sectionNavigationContentRef = useRef<HTMLDivElement>(null);

  const reactSections: ReactSection[] = children
    ? React.Children.toArray(children).map(ch => ({
        element: ch as ReactElement,
        settings: {
          nav_title:
            ((ch as ReactElement).props.navTitle as string) || "(no title)",
          nav_id:
            ((ch as ReactElement).props.navID as string) ||
            /*encodeURIComponent(
              (
                (ch as ReactElement).props.navTitle as string
              ).toLocaleLowerCase()
            ) ||*/
            ""
        }
      }))
    : [];

  const cockpitSections =
    reactSections.length === 0
      ? cockpitChildren?.reduce(
          (res: CockpitComponentData[][], component: CockpitComponentData) => {
            if (
              component.settings.nav_title &&
              component.children?.length > 0
            ) {
              res.push([{ ...component }]);
            } else {
              res[res.length - 1].push({ ...component });
            }
            return res;
          },
          [[]]
        ) || []
      : [];

  const sectionCount = reactSections.length + cockpitSections.length;
  const sectionTitles =
    reactSections.length > 0
      ? reactSections.map(s => s?.settings?.nav_title)
      : cockpitSections.map(s => s?.[0].settings?.nav_title as string);
  const sectionIDs =
    reactSections.length > 0
      ? reactSections.map(s => s?.settings?.nav_id)
      : cockpitSections.map(s => s?.[0].settings?.nav_id as string);

  const scrollAttr = direction === "vertical" ? "top" : "left";
  const offsetAttr = direction === "vertical" ? "offsetTop" : "offsetLeft";

  const computedStyle = window.getComputedStyle(document.documentElement);
  const baseFontSize = parseInt(
    computedStyle.getPropertyValue("font-size").match(/\d+/)?.[0] || "16"
  );
  const menuHeight =
    parseFloat(computedStyle.getPropertyValue("--menu-height") || "0") *
    baseFontSize;
  const SCROLL_PADDING =
    direction === "vertical"
      ? (!inOverlay ? menuHeight : 0) +
        (showTabs && isLargeScreen ? menuHeight : 0)
      : 0;

  //TODO: Implement with RemoveScroll, and make sure it works in overlays
  useEffect(() => {
    document.body.style.overflow = disableBodyScroll ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [disableBodyScroll]);

  useEffect(() => {
    const anchorHash = location.hash.replace("#", "");
    setTimeout(() => {
      if (anchorHash) {
        console.log("anchorHash", anchorHash, sectionIDs);
        const slideIndex = sectionIDs.findIndex(hash => hash === anchorHash);
        if (slideIndex > -1 && getSlide(slideIndex)) {
          container?.scrollTo({
            [scrollAttr]: getSlide(slideIndex)![offsetAttr] - SCROLL_PADDING,
            behavior: "smooth"
          });
        }
      } else {
        container?.scrollTo({
          [scrollAttr]: 0,
          behavior: "smooth"
        });
      }
    }, 1);
  }, [
    SCROLL_PADDING,
    container,
    getSlide,
    location,
    offsetAttr,
    scrollAttr,
    sectionIDs
  ]);

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      setIntroTimer(false);
    }, 2000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useLayoutEffect(() => {
    const slidePositions = Array(sectionCount)
      .fill(0)
      .map((_, i) => getSlide(i)?.[offsetAttr]);
    let nearestIndex =
      scrollPos === 0 && slidePositions[slidePositions.length - 1] === 0
        ? 0
        : slidePositions.findIndex(
            pos => (pos || 0) - SCROLL_PADDING - 50 > scrollPos
          );
    nearestIndex = nearestIndex === -1 ? sectionCount - 1 : nearestIndex - 1;
    if (_activeIndex.current !== nearestIndex) {
      setActiveIndex(Math.max(0, nearestIndex));
      _activeIndex.current = nearestIndex; //to avoid having activeIndex as dependency
    }
  }, [scrollPos, getSlide, offsetAttr, sectionCount, SCROLL_PADDING]);

  const onDrag: Handler<
    "drag",
    PointerEvent | MouseEvent | TouchEvent | KeyboardEvent
  > = ({ target, xy: [, y] }) => {
    if ((target as HTMLDivElement).id !== "section-navigation-touch-area")
      return;
    if (showMenuExpandButton && !menuBtnClicked) return;
    const el = sectionNavigationContentRef.current!;
    const elmTop = el.getBoundingClientRect().y;
    const elmBottom = elmTop + el.offsetHeight;
    const index = Math.max(
      0,
      Math.min(
        sectionCount - 1,
        Math.ceil(((y - elmTop) / (elmBottom - elmTop)) * sectionCount) - 1
      )
    );
    if (index !== hoveredIndex) setHoveredIndex(index);
  };

  const onDragStart: Handler<
    "drag",
    PointerEvent | MouseEvent | TouchEvent | KeyboardEvent
  > = ({ target }) => {
    if ((target as HTMLDivElement).id !== "section-navigation-touch-area")
      return;
    if (showMenuExpandButton && !menuBtnClicked) return;
    setHovered(true);
    setDisableBodyScroll(true);
  };

  const onDragEnd: Handler<
    "drag",
    PointerEvent | MouseEvent | TouchEvent | KeyboardEvent
  > = ({ target }) => {
    if ((target as HTMLDivElement).id !== "section-navigation-touch-area")
      return;
    if (showMenuExpandButton && !menuBtnClicked) return;
    const nextIndex = hoveredIndex;
    setDisableBodyScroll(false);
    if (
      nextIndex !== activeIndex &&
      nextIndex >= 0 &&
      nextIndex < sectionCount
    ) {
      const slide = contentTrack.current?.getContentSlide(nextIndex);
      if (slide) {
        navigate({ hash: sectionIDs[nextIndex] || "" }, { replace: true });
      }
    }
    setTimeout(() => {
      setHovered(false);
      setHoveredIndex(-1);
    }, 200);
  };

  const bindGestures = useGesture({
    onDrag: state => onDrag(state),
    onDragStart: state => onDragStart(state),
    onDragEnd: state => onDragEnd(state)
  });

  return (
    <>
      <div
        className={classNames("page-sections", "page-sections-" + direction, {
          "tab-navigation": showTabs
        })}
      >
        <DeepMemoContentTrack
          sections={reactSections}
          cockpitSections={cockpitSections}
          ref={contentTrack}
        />
      </div>
      {!showMenu && direction === "horizontal" /* back */ && (
        <NextSlideButton
          onClick={() => {
            const nextIndex = Math.max(activeIndex - 1, 0);

            setActiveIndex(nextIndex);
            if (getSlide(nextIndex)) {
              navigate(
                { hash: sectionIDs[nextIndex] || "" },
                { replace: true }
              );
            }
          }}
          slideDirection={direction}
          direction={-1}
          disabled={activeIndex <= 0}
        />
      )}
      {!showMenu &&
        (direction === "horizontal" || !isTouchDevice) /* forward */ && (
          <NextSlideButton
            onClick={() => {
              const nextIndex = Math.min(activeIndex + 1, sectionCount - 1);

              setActiveIndex(nextIndex);
              if (getSlide(nextIndex)) {
                navigate(
                  { hash: sectionIDs[nextIndex] || "" },
                  { replace: true }
                );
              }
            }}
            slideDirection={direction}
            direction={1}
            disabled={activeIndex >= sectionCount - 1}
          />
        )}
      {showMenuExpandButton &&
        !isLargeScreen &&
        direction === "vertical" &&
        sectionCount > 1 && (
          <button
            className={classNames("overlay-button")}
            id={"section-navigation-menu-button"}
            style={{
              position: "fixed",
              top: inOverlay
                ? !isTouchDevice
                  ? "4.75rem"
                  : "7rem"
                : "calc(var(--padding-top) + var(--menu-height) + 1.5rem)",
              right: "1.5rem"
            }}
            onClick={() => {
              setMenuBtnClicked(clicked => !clicked);
            }}
          >
            <Icon name="bars" />
          </button>
        )}
      {direction === "horizontal" && sectionCount > 1 && (
        <div className="section-navigation-horizontal-markers">
          {Array(sectionCount)
            .fill(0)
            .map((_, i) => (
              <div
                role="presentation"
                key={i}
                className={classNames(
                  "marker",
                  i === activeIndex ? "active" : false
                )}
                onClick={() => {
                  setActiveIndex(i);
                  if (getSlide(i)) {
                    navigate({ hash: sectionIDs[i] || "" }, { replace: true });
                  }
                }}
              ></div>
            ))}
        </div>
      )}
      {showMenu && direction === "vertical" && (
        <>
          <div
            className={classNames("section-navigation", className, {
              "tab-navigation": showTabs,
              "has-expand-button": showMenuExpandButton
            })}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className={classNames("section-navigation-overlay", {
                visible:
                  menuBtnClicked ||
                  (!showMenuExpandButton && hovered) ||
                  (introTimer && !isLargeScreen)
              })}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                if (menuBtnClicked) {
                  setMenuBtnClicked(false);
                }
              }}
            />

            <div
              className="section-navigation-touch-area"
              id="section-navigation-touch-area"
              {...bindGestures()}
              onMouseEnter={() => {
                setHovered(true);
              }}
              onMouseLeave={() => {
                setHovered(false);
              }}
            >
              <div
                className={classNames("section-navigation-content", {
                  full: menuBtnClicked || isLargeScreen,
                  visible:
                    menuBtnClicked ||
                    isVeryLargeScreen ||
                    (isLargeScreen && activeIndex === 0 && scrollPos < 100),
                  intro: introTimer && !isLargeScreen,
                  touching: !showMenuExpandButton && isTouchDevice && hovered
                })}
                ref={sectionNavigationContentRef}
                id="section-navigation-content"
              >
                {reactSections.length > 0
                  ? reactSections.map((_s, i) => (
                      <SectionMarker
                        hash={sectionIDs[i] || ""}
                        inOverlay={inOverlay}
                        title={
                          sectionTitles[i] ||
                          t("section-navigation.top-of-page")
                        }
                        key={i}
                        index={i}
                        isActive={i === activeIndex}
                        isHovered={
                          !showMenuExpandButton && hovered && i === hoveredIndex
                        }
                        setHovered={setHovered}
                        setHoveredIndex={setHoveredIndex}
                      />
                    ))
                  : cockpitSections.map((s, i) => (
                      <SectionMarker
                        hash={sectionIDs[i] || ""}
                        inOverlay={inOverlay}
                        title={
                          sectionTitles[i] ||
                          t("section-navigation.top-of-page")
                        }
                        key={i}
                        index={i}
                        isActive={i === activeIndex}
                        isHovered={
                          !showMenuExpandButton && hovered && i === hoveredIndex
                        }
                        setHovered={setHovered}
                        setHoveredIndex={setHoveredIndex}
                      />
                    ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SectionNavigation;
